import React from 'react';

interface OpsSubmissionsPanelProps {
  submissionsInbox: any;
  submissionsError: string | null;
  submissionsLoading: boolean;
  hideAbsentStudents: boolean;
  hiddenAbsentCount: number;
  expandedSubmissionStudentIds: Set<number>;
  redoRequested: number[];
  currentStepId: number | null;
  onRefresh: () => void;
  onToggleHideAbsentStudents: (nextValue: boolean) => void;
  onApproveEvidence: (evidenceId: number) => void;
  onGateDecision: (gateId: number, decision: 'allow' | 'revise', studentId: number) => void;
  onQualityDecision: (
    checkId: number,
    decision: 'clear' | 'assign_makeup' | 'group_reteach',
    studentId: number,
  ) => void;
  onRequestRedo: (studentId: number, stepId: number) => void;
  onToggleExpanded: (studentId: number) => void;
  formatSignalResponse: (response: Record<string, any> | null | undefined) => string;
}

export const OpsSubmissionsPanel: React.FC<OpsSubmissionsPanelProps> = ({
  submissionsInbox,
  submissionsError,
  submissionsLoading,
  hideAbsentStudents,
  hiddenAbsentCount,
  expandedSubmissionStudentIds,
  redoRequested,
  currentStepId,
  onRefresh,
  onToggleHideAbsentStudents,
  onApproveEvidence,
  onGateDecision,
  onQualityDecision,
  onRequestRedo,
  onToggleExpanded,
  formatSignalResponse,
}) => (
  <div className="bg-white rounded-lg border border-gray-200 p-5">
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="text-xs uppercase tracking-wide text-gray-400">Unified Submissions</p>
        <p className="text-sm text-gray-500 mt-1">Signals + evidence in one inbox</p>
      </div>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={!hideAbsentStudents}
            onChange={(event) => onToggleHideAbsentStudents(!event.target.checked)}
            className="rounded border-gray-300"
          />
          Include absent students
        </label>
        <button
          className="text-xs text-gray-500 hover:text-gray-700"
          onClick={onRefresh}
          type="button"
        >
          Refresh
        </button>
      </div>
    </div>

    {submissionsInbox?.summary ? (
      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        <span className="rounded-full border border-gray-200 px-2 py-1 text-gray-700">
          Students: {Number(submissionsInbox.summary.students_total || 0)}
        </span>
        <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-1 text-indigo-700">
          Signal: {Number(submissionsInbox.summary.signals_submitted || 0)}
        </span>
        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-emerald-700">
          Evidence: {Number(submissionsInbox.summary.evidence_submitted || 0)}
        </span>
        <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-1 text-blue-700">
          Both: {Number(submissionsInbox.summary.both_submitted || 0)}
        </span>
        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-emerald-700">
          Gate ready: {Number(submissionsInbox.summary.gate_allowed || 0)}
        </span>
        <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-amber-700">
          Gate review: {Number(submissionsInbox.summary.gate_review || 0)}
        </span>
        <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-1 text-rose-700">
          Gate revise: {Number(submissionsInbox.summary.gate_blocked || 0)}
        </span>
        <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-amber-700">
          Quality provisional: {Number(submissionsInbox.summary.quality_provisional || 0)}
        </span>
        <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-1 text-rose-700">
          Quality review: {Number(submissionsInbox.summary.quality_review || 0)}
        </span>
        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-emerald-700">
          Quality resolved: {Number(submissionsInbox.summary.quality_resolved || 0)}
        </span>
        {hideAbsentStudents && hiddenAbsentCount > 0 ? (
          <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-amber-700">
            Hidden absent: {hiddenAbsentCount}
          </span>
        ) : null}
      </div>
    ) : null}

    {submissionsError ? (
      <div className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        {submissionsError}
      </div>
    ) : null}

    {submissionsLoading ? (
      <div className="mt-4 text-sm text-gray-500">Loading submissions…</div>
    ) : !submissionsInbox || !Array.isArray(submissionsInbox.submissions) || submissionsInbox.submissions.length === 0 ? (
      <div className="mt-4 text-sm text-gray-500">
        {hideAbsentStudents ? 'No submissions from present students yet.' : 'No submissions yet.'}
      </div>
    ) : (
      <div className="mt-4 space-y-3">
        {submissionsInbox.submissions.map((rowRaw: any, index: number) => {
          const row = rowRaw && typeof rowRaw === 'object' ? rowRaw : {};
          const studentId = Number((row as any)?.student_id || 0);
          const safeStudentId = Number.isInteger(studentId) ? studentId : 0;
          const latestSignal = (row as any)?.signal?.latest;
          const latestEvidence = (row as any)?.evidence?.latest;
          const gate = (row as any)?.reflection_gate && typeof (row as any).reflection_gate === 'object'
            ? (row as any).reflection_gate
            : null;
          const quality = (row as any)?.submission_quality && typeof (row as any).submission_quality === 'object'
            ? (row as any).submission_quality
            : null;
          const gateReasonCodes = Array.isArray(gate?.reason_codes) ? gate.reason_codes : [];
          const qualityReasonCodes = Array.isArray(quality?.reason_codes) ? quality.reason_codes : [];
          const gateSuggestions = Array.isArray(gate?.suggestions) ? gate.suggestions : [];
          const qualitySuggestions = Array.isArray(quality?.suggestions) ? quality.suggestions : [];
          const gateId = Number(gate?.id || 0);
          const qualityId = Number(quality?.id || 0);
          const canActOnGate = Number.isInteger(gateId) && gateId > 0 && safeStudentId > 0;
          const canActOnQuality = Number.isInteger(qualityId) && qualityId > 0 && safeStudentId > 0;
          const canRequestRedo = Boolean(currentStepId && safeStudentId > 0);
          const gateStatus = String(gate?.final_status || '').trim().toLowerCase();
          const qualityStatus = String(quality?.status || '').trim().toLowerCase();
          const gateNeedsReview = gateStatus === 'needs_teacher_review';
          const gateBlocked = gateStatus === 'blocked_revision';
          const gateAllowed = gateStatus === 'allowed';
          const qualityProvisional = qualityStatus === 'provisional';
          const qualityReview = qualityStatus === 'needs_teacher_review';
          const qualityResolved = qualityStatus === 'resolved';
          const qualityAccepted = qualityStatus === 'accepted';
          const signalReasoning = String(
            latestSignal?.response?.reasoning_text
            || latestSignal?.response?.reasoning
            || '',
          ).trim();
          const signalConfidenceRaw = latestSignal?.response?.confidence_level ?? latestSignal?.response?.confidence;
          const signalConfidence = signalConfidenceRaw !== undefined && signalConfidenceRaw !== null
            ? Number(signalConfidenceRaw)
            : null;
          const evidenceText = String(latestEvidence?.content?.text || latestEvidence?.content?.url || '').trim();
          const isExpanded = safeStudentId > 0 ? expandedSubmissionStudentIds.has(safeStudentId) : false;
          const rowKey = safeStudentId > 0 ? `submission-${safeStudentId}` : `submission-row-${index}`;
          return (
            <div key={rowKey} className="rounded border border-gray-200 p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">{(row as any)?.student_name || `Student ${safeStudentId || 'unknown'}`}</p>
                  <p className="text-xs text-gray-500">
                    Signal attempts: {Number((row as any)?.signal?.attempt_count || 0)} | Evidence attempts: {Number((row as any)?.evidence?.attempt_count || 0)}
                  </p>
                  {gate ? (
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      <span
                        className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                          gateAllowed
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : gateBlocked
                              ? 'border-rose-200 bg-rose-50 text-rose-700'
                              : 'border-amber-200 bg-amber-50 text-amber-700'
                        }`}
                      >
                        {gateAllowed ? 'Ready for reflection' : gateBlocked ? 'Revision required' : 'Teacher review'}
                      </span>
                      <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] text-gray-600">
                        Triage: {Number((row as any)?.triage_score || 0)}
                      </span>
                      {quality ? (
                        <span
                          className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                            qualityResolved || qualityAccepted
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                              : qualityReview
                                ? 'border-rose-200 bg-rose-50 text-rose-700'
                                : 'border-amber-200 bg-amber-50 text-amber-700'
                          }`}
                        >
                          {qualityResolved
                            ? 'Quality resolved'
                            : qualityAccepted
                              ? 'Quality accepted'
                              : qualityReview
                                ? 'Quality teacher review'
                                : 'Quality provisional'}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  {latestEvidence && String(latestEvidence.status || '').toLowerCase() === 'submitted' ? (
                    <button
                      type="button"
                      onClick={() => onApproveEvidence(latestEvidence.id)}
                      className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                    >
                      Approve
                    </button>
                  ) : null}
                  {gate && !gateAllowed ? (
                    <button
                      type="button"
                      onClick={() => onGateDecision(gateId, 'allow', safeStudentId)}
                      className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200"
                      disabled={!canActOnGate}
                    >
                      Allow reflection
                    </button>
                  ) : null}
                  {gate && !gateBlocked ? (
                    <button
                      type="button"
                      onClick={() => onGateDecision(gateId, 'revise', safeStudentId)}
                      className="text-xs px-2 py-1 rounded bg-rose-100 text-rose-700 hover:bg-rose-200"
                      disabled={!canActOnGate}
                    >
                      Require revision
                    </button>
                  ) : null}
                  {quality && !qualityResolved ? (
                    <button
                      type="button"
                      onClick={() => onQualityDecision(qualityId, 'clear', safeStudentId)}
                      className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                      disabled={!canActOnQuality}
                    >
                      Clear quality
                    </button>
                  ) : null}
                  {quality && !qualityResolved ? (
                    <button
                      type="button"
                      onClick={() => onQualityDecision(qualityId, 'assign_makeup', safeStudentId)}
                      className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700 hover:bg-amber-200"
                      disabled={!canActOnQuality}
                    >
                      Assign makeup
                    </button>
                  ) : null}
                  {quality && !qualityResolved ? (
                    <button
                      type="button"
                      onClick={() => onQualityDecision(qualityId, 'group_reteach', safeStudentId)}
                      className="text-xs px-2 py-1 rounded bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                      disabled={!canActOnQuality}
                    >
                      Group reteach
                    </button>
                  ) : null}
                  {canRequestRedo ? (
                    <button
                      type="button"
                      onClick={() => onRequestRedo(safeStudentId, Number(currentStepId))}
                      className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700 hover:bg-amber-200"
                    >
                      {redoRequested.includes(safeStudentId) ? 'Redo requested' : 'Request redo'}
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <div className="rounded border border-indigo-100 bg-indigo-50 p-2">
                  <p className="text-[11px] uppercase tracking-wide text-indigo-700">Latest Signal</p>
                  <p className="mt-1 text-sm text-indigo-900 whitespace-pre-wrap break-words">
                    {formatSignalResponse(latestSignal?.response || null)}
                  </p>
                  <p className="mt-1 text-xs text-indigo-700">
                    {latestSignal?.submitted_at ? new Date(latestSignal.submitted_at).toLocaleString() : 'No signal timestamp'}
                  </p>
                  {signalReasoning ? (
                    <p className="mt-1 text-xs text-indigo-800 whitespace-pre-wrap break-words">Reasoning: {signalReasoning}</p>
                  ) : null}
                  {signalConfidence !== null && Number.isFinite(signalConfidence) ? (
                    <p className="mt-1 text-xs text-indigo-800">Confidence: {signalConfidence}</p>
                  ) : null}
                </div>

                <div className="rounded border border-emerald-100 bg-emerald-50 p-2">
                  <p className="text-[11px] uppercase tracking-wide text-emerald-700">Latest Evidence</p>
                  <p className="mt-1 text-sm text-emerald-900 whitespace-pre-wrap break-words">
                    {evidenceText || '—'}
                  </p>
                  <p className="mt-1 text-xs text-emerald-700">
                    Status: {String(latestEvidence?.status || 'none')}
                    {latestEvidence?.submitted_at ? ` | ${new Date(latestEvidence.submitted_at).toLocaleString()}` : ''}
                  </p>
                </div>
              </div>

              {(gate && (gateNeedsReview || gateBlocked)) || (quality && (qualityProvisional || qualityReview)) ? (
                <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-2">
                  {gateReasonCodes.length > 0 ? (
                    <p className="text-xs text-amber-800">
                      Reasons: {gateReasonCodes.join(', ')}
                    </p>
                  ) : null}
                  {qualityReasonCodes.length > 0 ? (
                    <p className="mt-1 text-xs text-amber-800">
                      Quality reasons: {qualityReasonCodes.join(', ')}
                    </p>
                  ) : null}
                  {gateSuggestions.length > 0 ? (
                    <p className="mt-1 text-xs text-amber-900">
                      Suggestion: {String(gateSuggestions[0])}
                    </p>
                  ) : null}
                  {qualitySuggestions.length > 0 ? (
                    <p className="mt-1 text-xs text-amber-900">
                      Quality suggestion: {String(qualitySuggestions[0])}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {(Number((row as any)?.signal?.attempt_count || 0) > 1 || Number((row as any)?.evidence?.attempt_count || 0) > 1) ? (
                <button
                  type="button"
                  className="mt-3 text-xs text-gray-500 hover:text-gray-700"
                  onClick={() => onToggleExpanded(safeStudentId)}
                  disabled={safeStudentId <= 0}
                >
                  {isExpanded ? 'Hide full attempt history' : 'Show full attempt history'}
                </button>
              ) : null}

              {isExpanded ? (
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <div className="rounded border border-gray-200 p-2">
                    <p className="text-xs font-medium text-gray-700">Signal attempts</p>
                    <div className="mt-2 space-y-2">
                      {((row as any)?.signal?.recent_attempts || []).slice(1).map((attempt: any) => (
                        <div key={`signal-attempt-${attempt.id}`} className="text-xs text-gray-700">
                          <div>{attempt.submitted_at ? new Date(attempt.submitted_at).toLocaleString() : '—'}</div>
                          <div className="whitespace-pre-wrap break-words">{formatSignalResponse(attempt.response || null)}</div>
                        </div>
                      ))}
                      {Number((row as any)?.signal?.attempt_count || 0) <= 1 ? (
                        <div className="text-xs text-gray-500">No older signal attempts.</div>
                      ) : null}
                    </div>
                  </div>
                  <div className="rounded border border-gray-200 p-2">
                    <p className="text-xs font-medium text-gray-700">Evidence attempts</p>
                    <div className="mt-2 space-y-2">
                      {((row as any)?.evidence?.recent_attempts || []).slice(1).map((attempt: any) => (
                        <div key={`evidence-attempt-${attempt.id}`} className="text-xs text-gray-700">
                          <div>{attempt.submitted_at ? new Date(attempt.submitted_at).toLocaleString() : '—'}</div>
                          <div>Status: {String(attempt.status || 'submitted')}</div>
                          <div className="whitespace-pre-wrap break-words">{String(attempt.content?.text || attempt.content?.url || '—')}</div>
                        </div>
                      ))}
                      {Number((row as any)?.evidence?.attempt_count || 0) <= 1 ? (
                        <div className="text-xs text-gray-500">No older evidence attempts.</div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    )}
  </div>
);
